import { type Component, createMemo, createSignal, onCleanup, onMount, Show } from 'solid-js'
import { Portal } from 'solid-js/web'
import { twMerge } from 'tailwind-merge'
import Icon from '~/components/Icon'
import Tooltip from '~/components/Tooltip'
import { usePresence } from '~/hooks/usePresence'
import type { MenuItemType, MenuPropsType } from '~/types/Menu'
import { getGsap, MOTION } from '~/utils/gsap'

const Menu: Component<MenuPropsType> = props => {
  const [isOpen, setIsOpen] = createSignal(false)
  const [triggerRect, setTriggerRect] = createSignal<DOMRect | null>(null)
  let triggerRef: HTMLDivElement | undefined
  let menuRef: HTMLDivElement | undefined

  const presence = usePresence({
    open: () => isOpen(),
    enter: el => {
      const g = getGsap()
      if (!g) return
      return g.fromTo(el, { opacity: 0, scale: 0.95, y: -4, transformOrigin: 'top right' }, { opacity: 1, scale: 1, y: 0, duration: MOTION.enter, ease: MOTION.ease })
    },
    exit: el => {
      const g = getGsap()
      if (!g) return
      return g.to(el, { opacity: 0, scale: 0.97, duration: MOTION.exit, ease: 'power2.in' })
    },
    onEntered: () => {
      if (menuRef) menuRef.style.opacity = ''
    }
  })

  const toggle = (_e?: MouseEvent) => {
    if (props.contextual) return

    if (!isOpen() && triggerRef) {
      setTriggerRect(triggerRef.getBoundingClientRect())
    }
    setIsOpen(!isOpen())
  }

  const handleContextMenu = (e: MouseEvent) => {
    if (props.contextual) {
      e.preventDefault()
      if (!isOpen() && triggerRef) {
        setTriggerRect(triggerRef.getBoundingClientRect())
      }
      setIsOpen(true)
    }
  }

  const close = () => {
    setIsOpen(false)
  }

  const hasOnlyIcons = () => {
    return props.items.every(item => !item.label && item.icon)
  }

  const menuClasses = createMemo(() => twMerge('z-50 overflow-hidden rounded-lg border border-border bg-surface shadow-lg', hasOnlyIcons() ? 'w-auto min-w-0' : 'min-w-45'))

  const getItemClasses = (color?: MenuItemType['color']) =>
    createMemo(() => twMerge(`flex w-full items-center justify-start ${hasOnlyIcons() ? 'gap-1 px-2' : 'gap-2 px-4'} py-2 text-sm`, getItemColor(color)))

  const getLabelClasses = createMemo(() => twMerge('truncate self-center', hasOnlyIcons() ? 'max-w-30' : 'max-w-40'))

  const handleScrollOrResize = () => {
    if (isOpen()) {
      close()
    }
  }

  const handleClickOutside = (e: MouseEvent) => {
    if (isOpen() && triggerRef && menuRef && !triggerRef.contains(e.target as Node) && !menuRef.contains(e.target as Node)) {
      close()
    }
  }
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && isOpen()) {
      close()
    }
  }

  onMount(() => {
    window.addEventListener('scroll', handleScrollOrResize, true)
    window.addEventListener('resize', handleScrollOrResize)
    document.addEventListener('click', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)
  })

  onCleanup(() => {
    window.removeEventListener('scroll', handleScrollOrResize, true)
    window.removeEventListener('resize', handleScrollOrResize)
    document.removeEventListener('click', handleClickOutside)
    document.removeEventListener('keydown', handleKeyDown)
  })

  /** Alto aproximado del menú: sirve para decidir el lado antes de medirlo. */
  const estimatedHeight = () => props.items.length * 36 + 8

  /**
   * Coloca el menú donde quepa. Antes salía siempre hacia abajo y a la derecha
   * del disparador, así que un menú al pie de la pantalla —el de usuario del
   * menú lateral, sin ir más lejos— se abría fuera de la ventana: parecía que
   * el botón no hiciera nada.
   */
  const getMenuPosition = () => {
    const rect = triggerRect()
    if (!rect) return {}

    const margin = 8
    const spaceBelow = window.innerHeight - rect.bottom
    const spaceAbove = rect.top
    // Hacia arriba sólo si abajo no cabe y arriba hay más sitio. Se ancla por
    // `bottom` para no depender de saber el alto exacto.
    const openUp = spaceBelow < estimatedHeight() + margin && spaceAbove > spaceBelow

    // Lo mismo en horizontal: alineado a la derecha del disparador salvo que
    // eso lo saque por la izquierda (pasa con el menú lateral contraído).
    const minWidth = 180
    const overflowsLeft = rect.right - minWidth < margin

    return {
      position: 'fixed' as const,
      ...(openUp ? { bottom: `${window.innerHeight - rect.top + 4}px` } : { top: `${rect.bottom + 4}px` }),
      ...(overflowsLeft ? { left: `${Math.max(margin, rect.left)}px` } : { right: `${Math.max(margin, window.innerWidth - rect.right)}px` }),
      'max-height': `${Math.max(160, (openUp ? spaceAbove : spaceBelow) - margin * 2)}px`,
      'overflow-y': 'auto' as const
    }
  }

  const getItemColor = (color: MenuItemType['color'] = 'default') => {
    switch (color) {
      case 'primary':
        return 'text-primary hover:bg-primary-soft'
      case 'danger':
        return 'text-danger hover:bg-danger-soft'
      default:
        return 'text-foreground hover:bg-surface-hover'
    }
  }

  const renderItem = (item: MenuItemType) => (
    <button
      type="button"
      class={getItemClasses(item.color)()}
      aria-label={item.tooltip || item.label}
      onClick={() => {
        item.onClick?.()
        close()
      }}
    >
      {item.icon && <Icon icon={item.icon} size={16} class="shrink-0 self-center" />}
      {item.label && <span class={getLabelClasses()}>{item.label}</span>}
    </button>
  )

  return (
    <>
      <div
        ref={triggerRef}
        onClick={e => {
          e.stopPropagation()
          toggle()
        }}
        onContextMenu={handleContextMenu}
      >
        {props.trigger}
      </div>

      <Show when={presence.mounted()}>
        <Portal>
          <div
            ref={el => {
              menuRef = el
              presence.setRef(el)
            }}
            class={menuClasses()}
            style={{ ...getMenuPosition(), opacity: 0 }}
          >
            <div>
              {props.items.map(item =>
                item.tooltip ? (
                  <Tooltip content={item.tooltip} placement="top" triggerClass="block w-full">
                    {renderItem(item)}
                  </Tooltip>
                ) : (
                  renderItem(item)
                )
              )}
            </div>
          </div>
        </Portal>
      </Show>
    </>
  )
}

export default Menu
